import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Brain, TrendingDown, CalendarX, UserX, AlertTriangle, Users, Check, X, Zap, CheckCircle2, ListTodo, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const IMPACT = { alto: "bg-rose-500/15 text-rose-600", medio: "bg-amber-500/15 text-amber-600", bajo: "bg-sky-500/15 text-sky-600" };
const CAT_ICON = { liderazgo: UserX, estructura: Users, rotacion: TrendingDown, ausentismo: CalendarX };

export default function PeopleIntelligence() {
  const [data, setData] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/people/intelligence"),
      api.get("/people/action-items").catch(() => ({ data: [] })),
    ]).then(([intel, act]) => { setData(intel.data); setActions(act.data || []); })
      .catch(() => setError(true)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const recAction = async (rec, action) => {
    setBusy(rec.id + action);
    try {
      await api.post(`/people/recommendations/${rec.id}/action`, { action });
      toast.success(action === "accept" ? "Acción creada y en curso" : "Recomendación descartada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(null); }
  };

  const completeAction = async (a) => {
    setBusy("done" + a.id);
    try { await api.patch(`/people/action-items/${a.id}`, { status: "done" }); toast.success("Acción completada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(null); }
  };

  const m = data?.metrics || {};
  const cards = [
    { l: "Dotación activa", v: m.headcount, icon: Brain, cls: "text-violet" },
    { l: "Rotación", v: `${m.turnover_pct ?? 0}%`, icon: TrendingDown, cls: "text-amber-500" },
    { l: "Días de ausentismo", v: m.ausentismo_dias, icon: CalendarX, cls: "text-sky-500" },
    { l: "Sin jefe asignado", v: m.sin_jefe_asignado, icon: UserX, cls: "text-rose-500" },
  ];
  const recs = data?.recommendations || [];
  const openActions = actions.filter((a) => a.status === "open");

  return (
    <OSLayout title="People Intelligence">
      <div className="max-w-4xl space-y-6" data-testid="people-intelligence">
        <div>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Brain className="w-3.5 h-3.5" /> OMNIA People Suite · IA</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">People Intelligence</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">Detección de rotación, ausentismo y equipos sin líder, con un Centro de Acción para ejecutar las recomendaciones de la IA con un clic.</p>
        </div>

        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          error ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">No disponible para tu rol.</div> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pi-metrics">
              {cards.map((c) => (
                <div key={c.l} className="bg-white border border-navy/10 rounded-2xl p-4">
                  <c.icon className={`w-5 h-5 ${c.cls}`} />
                  <div className="text-2xl font-medium text-navy mt-2">{c.v}</div>
                  <div className="text-xs text-navy/45">{c.l}</div>
                </div>
              ))}
            </div>

            {(m.areas_sin_lider || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="pi-areas-sin-lider">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="text-sm text-navy/70">Áreas sin líder asignado: <b>{m.areas_sin_lider.join(", ")}</b></div>
              </div>
            )}

            {/* Centro de Acción — recomendaciones ejecutables */}
            <div data-testid="pi-action-center">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Zap className="w-4 h-4 text-violet" />
                <h3 className="font-editorial text-2xl">Centro de Acción</h3>
                <span className="text-xs text-navy/40">· acepta o descarta las recomendaciones de la IA</span>
                <Link to="/os/people-actions" className="ml-auto btn btn-ghost text-xs gap-1.5 text-violet" data-testid="pi-open-actions-panel"><ListTodo className="w-3.5 h-3.5" /> Panel de Acciones RH <ArrowUpRight className="w-3 h-3" /></Link>
              </div>
              {recs.length === 0 ? (
                <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40" data-testid="pi-recs-empty">
                  Sin recomendaciones activas. Todo en orden por ahora. 🎉
                </div>
              ) : (
                <div className="space-y-3">
                  {recs.map((r) => {
                    const Icon = CAT_ICON[r.category] || Brain;
                    const actioned = r.status === "accepted" || r.status === "done";
                    return (
                      <div key={r.id} className="bg-white border border-navy/10 rounded-2xl p-4 flex items-start gap-3" data-testid={`pi-rec-${r.id}`}>
                        <div className="w-9 h-9 rounded-xl bg-violet/10 flex items-center justify-center shrink-0"><Icon className="w-4.5 h-4.5 text-violet" /></div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-navy">{r.title}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${IMPACT[r.impact] || "bg-navy/10 text-navy/50"}`}>impacto {r.impact}</span>
                            {actioned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {r.status === "done" ? "Completada" : "Aceptada"}</span>}
                          </div>
                          <p className="text-sm text-navy/55 mt-1">{r.detail}</p>
                          {!actioned && (
                            <div className="flex items-center gap-2 mt-3">
                              <button onClick={() => recAction(r, "accept")} disabled={busy === r.id + "accept"} className="btn btn-primary text-xs gap-1.5" data-testid={`pi-rec-accept-${r.id}`}>
                                {busy === r.id + "accept" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {r.action_label || "Aceptar y ejecutar"}
                              </button>
                              <button onClick={() => recAction(r, "reject")} disabled={busy === r.id + "reject"} className="btn btn-ghost text-xs gap-1.5 text-navy/50" data-testid={`pi-rec-reject-${r.id}`}>
                                <X className="w-3.5 h-3.5" /> Descartar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Acciones en curso */}
            {openActions.length > 0 && (
              <div data-testid="pi-open-actions">
                <div className="flex items-center gap-2 mb-3"><ListTodo className="w-4 h-4 text-violet" /><h3 className="font-editorial text-xl">Acciones en curso ({openActions.length})</h3></div>
                <div className="space-y-2">
                  {openActions.map((a) => (
                    <div key={a.id} className="bg-white border border-navy/10 rounded-xl p-3 flex items-center gap-3" data-testid={`pi-action-${a.id}`}>
                      <div className="flex-1 min-w-0"><div className="text-sm font-medium text-navy truncate">{a.title}</div><div className="text-xs text-navy/40">Creada por {a.created_by_name} · {(a.created_at || "").slice(0, 10)}</div></div>
                      <button onClick={() => completeAction(a)} disabled={busy === "done" + a.id} className="btn btn-ghost text-xs gap-1.5 text-emerald-600" data-testid={`pi-action-done-${a.id}`}>
                        {busy === "done" + a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Marcar hecho
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gradient-to-br from-navy to-navy-soft text-cream rounded-2xl p-6" data-testid="pi-insight">
              <div className="overline text-cream/50 mb-3 flex items-center gap-2"><Brain className="w-3.5 h-3.5" /> Recomendaciones de IA</div>
              <div className="text-sm leading-relaxed whitespace-pre-wrap">{data.insight}</div>
            </div>
          </>
        )}
      </div>
    </OSLayout>
  );
}
